/**
 * FB36 — the mold-inspection scheduling grid and the $200 inspection-fee gate.
 *
 * Shared by BOTH sides on purpose, so the call taker's calendar and the server
 * that books into it can never disagree about what a valid slot is:
 *   - `functions/api/mold/availability.ts` + `book.ts` (grid generation, on-grid
 *     validation, the do-not-go gate),
 *   - `src/components/job/mold-schedule-modal.tsx` (month calendar, slot list,
 *     fee panel).
 *
 * ── Timezone rule ──────────────────────────────────────────────────────────
 * The business day is a WALL-CLOCK fact in America/New_York ("we inspect from
 * 8 to 6"), but instants are stored in UTC (`mold_appointments.starts_at` is
 * timestamptz). So the grid is generated FROM the local wall clock, per day,
 * and converted to UTC using that day's own offset. Never by adding a fixed
 * number of hours — twice a year that is wrong by an hour, and an 8 AM
 * appointment that silently becomes 7 AM is a customer standing in a driveway
 * alone.
 *
 * The 08:00–18:00 window never contains the 02:00 DST transitions, so no slot
 * is ever ambiguous or nonexistent; only the day's UTC offset changes
 * (2026-03-08 08:00 ET = 12:00Z, 2026-03-07 08:00 ET = 13:00Z).
 *
 * No DOM, no React, no date-fns-tz — just `Intl`, which every one of our
 * runtimes (browser, Workers, Node, Hermes with the full ICU build) has.
 */

/** Every mold appointment is scheduled against this zone's wall clock. */
export const MOLD_TZ = 'America/New_York';
/** Business window, local wall clock: first slot starts at 08:00… */
export const MOLD_DAY_START_HOUR = 8;
/** …and the last slot ENDS at 18:00. */
export const MOLD_DAY_END_HOUR = 18;
/** Slot length the owner asked for: two-hour arrival windows. */
export const MOLD_SLOT_HOURS = 2;
/** 08–10, 10–12, 12–14, 14–16, 16–18. */
export const MOLD_SLOTS_PER_DAY =
  (MOLD_DAY_END_HOUR - MOLD_DAY_START_HOUR) / MOLD_SLOT_HOURS;

/** The local start hours of the grid: [8, 10, 12, 14, 16]. */
export const MOLD_SLOT_HOURS_OF_DAY: number[] = Array.from(
  { length: MOLD_SLOTS_PER_DAY },
  (_, i) => MOLD_DAY_START_HOUR + i * MOLD_SLOT_HOURS,
);

/** `staff_role_keys` entry that marks the primary mold technician (migration 0161). */
export const MOLD_TECHNICIAN_ROLE_KEY = 'mold_technician';

/** `job_fees.fee_key` for the inspection fee that gates the visit. */
export const MOLD_FEE_KEY = 'mold_inspection';
/** The fee itself. One number, one place — the UI copy and the server both read it. */
export const MOLD_FEE_AMOUNT = 200;
/** `job_fees.label` written for the row. */
export const MOLD_FEE_LABEL = 'Mold Inspection Fee';

/** Statuses that occupy a technician's slot (must match migration 0161's partial index). */
export const MOLD_ACTIVE_STATUSES = ['booked', 'confirmed'] as const;

export type MoldAppointmentStatus =
  | 'offered' | 'booked' | 'confirmed' | 'completed' | 'canceled';

// ── Timezone primitives ─────────────────────────────────────────────────────

interface WallClock {
  year: number; month: number; day: number;
  hour: number; minute: number; second: number;
}

const PARTS_FMT_CACHE = new Map<string, Intl.DateTimeFormat>();
function partsFormatter(tz: string): Intl.DateTimeFormat {
  let f = PARTS_FMT_CACHE.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    PARTS_FMT_CACHE.set(tz, f);
  }
  return f;
}

/** What the wall clock in `tz` reads at this instant. */
export function wallClockIn(instant: Date | number, tz: string = MOLD_TZ): WallClock {
  const parts = partsFormatter(tz).formatToParts(
    typeof instant === 'number' ? new Date(instant) : instant,
  );
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? '0');
  // `hour12: false` renders midnight as "24" in some ICU versions.
  const hour = get('hour') % 24;
  return {
    year: get('year'), month: get('month'), day: get('day'),
    hour, minute: get('minute'), second: get('second'),
  };
}

/** The zone's UTC offset (ms, east-positive) at a given instant. */
export function tzOffsetMs(instant: Date | number, tz: string = MOLD_TZ): number {
  const ms = typeof instant === 'number' ? instant : instant.getTime();
  const w = wallClockIn(ms, tz);
  return Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second) - ms;
}

/**
 * Local wall-clock time → the UTC instant it names.
 *
 * Two passes: guess with the offset in force at the *naive* timestamp, then
 * re-read the offset at the guessed instant and correct. That converges for
 * every real zone because a DST shift is at most an hour and our slots are
 * never within an hour of one.
 */
export function zonedWallTimeToUtc(
  ymd: string, hour: number, minute = 0, tz: string = MOLD_TZ,
): Date {
  const [y, m, d] = ymd.split('-').map(Number);
  const naive = Date.UTC(y, m - 1, d, hour, minute, 0, 0);
  let ts = naive - tzOffsetMs(naive, tz);
  ts = naive - tzOffsetMs(ts, tz);
  return new Date(ts);
}

/** The `YYYY-MM-DD` the instant falls on, in the zone's local calendar. */
export function zonedYmd(instant: Date | string | number, tz: string = MOLD_TZ): string {
  const d = typeof instant === 'string' ? new Date(instant) : instant;
  const w = wallClockIn(d, tz);
  return `${w.year}-${pad2(w.month)}-${pad2(w.day)}`;
}

function pad2(n: number): string { return String(n).padStart(2, '0'); }

/** Shift a `YYYY-MM-DD` by whole calendar days (DST-proof: pure date math). */
export function addDaysYmd(ymd: string, days: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d));
  t.setUTCDate(t.getUTCDate() + days);
  return `${t.getUTCFullYear()}-${pad2(t.getUTCMonth() + 1)}-${pad2(t.getUTCDate())}`;
}

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;
/** True for a syntactically valid, real `YYYY-MM-DD`. */
export function isYmd(value: string): boolean {
  if (!YMD_RE.test(value)) return false;
  const [y, m, d] = value.split('-').map(Number);
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCMonth() === m - 1 && t.getUTCDate() === d;
}

// ── The slot grid ───────────────────────────────────────────────────────────

export interface MoldSlot {
  /** UTC ISO instant the window opens — the value stored in `starts_at`. */
  startsAt: string;
  /** UTC ISO instant it closes (`startsAt` + 2h). */
  endsAt: string;
  /** Local calendar day the slot belongs to. */
  date: string;
  /** What the call taker reads to the customer, e.g. "8:00 – 10:00 AM". */
  label: string;
  /** False when the primary mold technician already has this window. */
  available: boolean;
  /** Set when `available` is false: which job holds it (for staff context). */
  takenByJobId?: string | null;
}

export interface MoldDay {
  /** `YYYY-MM-DD`, local. */
  date: string;
  slots: MoldSlot[];
  /** How many of the five are still open — what the month grid prints. */
  openCount: number;
}

/** "8:00 – 10:00 AM" / "4:00 – 6:00 PM" — plain enough to read down a phone line. */
export function slotLabel(startHour: number, endHour: number = startHour + MOLD_SLOT_HOURS): string {
  const h12 = (h: number) => (h % 12 === 0 ? 12 : h % 12);
  const mer = (h: number) => (h < 12 ? 'AM' : 'PM');
  return mer(startHour) === mer(endHour)
    ? `${h12(startHour)}:00 – ${h12(endHour)}:00 ${mer(endHour)}`
    : `${h12(startHour)}:00 ${mer(startHour)} – ${h12(endHour)}:00 ${mer(endHour)}`;
}

/** The five UTC slot starts for one local day. Nothing about availability yet. */
export function slotStartsForDay(ymd: string, tz: string = MOLD_TZ): string[] {
  return MOLD_SLOT_HOURS_OF_DAY.map((h) => zonedWallTimeToUtc(ymd, h, 0, tz).toISOString());
}

/** A booked/confirmed window belonging to the technician. */
export interface BusyWindow {
  startsAt: string;
  jobId?: string | null;
}

/**
 * The day's five windows with the technician's existing appointments
 * subtracted. Matching is by exact instant: everything on both sides is
 * generated from (or validated against) the same grid, so a booking can only
 * ever land on a slot start.
 */
export function moldSlotsForDay(
  ymd: string, busy: BusyWindow[] = [], tz: string = MOLD_TZ,
): MoldDay {
  const taken = new Map<number, string | null>();
  for (const b of busy) {
    const t = new Date(b.startsAt).getTime();
    if (!Number.isNaN(t)) taken.set(t, b.jobId ?? null);
  }

  const slots = MOLD_SLOT_HOURS_OF_DAY.map((h) => {
    const start = zonedWallTimeToUtc(ymd, h, 0, tz);
    const end = zonedWallTimeToUtc(ymd, h + MOLD_SLOT_HOURS, 0, tz);
    const key = start.getTime();
    const isTaken = taken.has(key);
    const slot: MoldSlot = {
      startsAt: start.toISOString(),
      endsAt: end.toISOString(),
      date: ymd,
      label: slotLabel(h, h + MOLD_SLOT_HOURS),
      available: !isTaken,
    };
    if (isTaken) slot.takenByJobId = taken.get(key) ?? null;
    return slot;
  });

  return { date: ymd, slots, openCount: slots.filter((s) => s.available).length };
}

/** `days` consecutive local days starting at `startYmd` (month prefetch). */
export function moldSlotsForRange(
  startYmd: string, days: number, busy: BusyWindow[] = [], tz: string = MOLD_TZ,
): MoldDay[] {
  const out: MoldDay[] = [];
  for (let i = 0; i < days; i++) {
    const ymd = addDaysYmd(startYmd, i);
    // Cheap enough at month scale; keeps the per-day function the only grid logic.
    out.push(moldSlotsForDay(ymd, busy, tz));
  }
  return out;
}

/** UTC bounds [start, end) covering `days` local days — what the DB query filters on. */
export function rangeBoundsUtc(
  startYmd: string, days: number, tz: string = MOLD_TZ,
): { fromIso: string; toIso: string } {
  return {
    fromIso: zonedWallTimeToUtc(startYmd, 0, 0, tz).toISOString(),
    toIso: zonedWallTimeToUtc(addDaysYmd(startYmd, days), 0, 0, tz).toISOString(),
  };
}

/**
 * Is this instant a legal slot start? The booking endpoint's first gate — it
 * stops a hand-crafted POST from wedging an appointment at 09:17 that the
 * calendar can then never show, and keeps the
 * `(technician_user_id, starts_at)` uniqueness in migration 0161 meaningful
 * (a grid-aligned start is the whole reason a plain unique index can stand in
 * for a range-overlap constraint).
 */
export function isMoldSlotStart(startsAt: string, tz: string = MOLD_TZ): boolean {
  const d = new Date(startsAt);
  if (Number.isNaN(d.getTime())) return false;
  const w = wallClockIn(d, tz);
  if (w.minute !== 0 || w.second !== 0 || d.getTime() % 60_000 !== 0) return false;
  if (!MOLD_SLOT_HOURS_OF_DAY.includes(w.hour)) return false;
  // Round-trip guard: the wall clock we read must map back to this instant.
  const ymd = `${w.year}-${pad2(w.month)}-${pad2(w.day)}`;
  return zonedWallTimeToUtc(ymd, w.hour, 0, tz).getTime() === d.getTime();
}

/** The matching end instant for a validated slot start. */
export function moldSlotEnd(startsAt: string, tz: string = MOLD_TZ): string {
  const w = wallClockIn(new Date(startsAt), tz);
  const ymd = `${w.year}-${pad2(w.month)}-${pad2(w.day)}`;
  return zonedWallTimeToUtc(ymd, w.hour + MOLD_SLOT_HOURS, 0, tz).toISOString();
}

/** "Tue, Sep 8 · 8:00 – 10:00 AM" — for audit notes and the modal header. */
export function describeMoldSlot(startsAt: string, tz: string = MOLD_TZ): string {
  const d = new Date(startsAt);
  const day = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  }).format(d);
  const w = wallClockIn(d, tz);
  return `${day} · ${slotLabel(w.hour, w.hour + MOLD_SLOT_HOURS)} ET`;
}

// ── The $200 inspection-fee gate ────────────────────────────────────────────

/** The `job_fees` row shape both sides reason about. */
export interface MoldFeeState {
  amount?: number | null;
  sent_at?: string | null;
  paid_at?: string | null;
}

export type MoldFeeStatus = 'not_sent' | 'sent' | 'paid';

export function moldFeeStatus(fee: MoldFeeState | null | undefined): MoldFeeStatus {
  if (fee?.paid_at) return 'paid';
  if (fee?.sent_at) return 'sent';
  return 'not_sent';
}

export function isMoldFeePaid(fee: MoldFeeState | null | undefined): boolean {
  return moldFeeStatus(fee) === 'paid';
}

/**
 * The do-not-go gate, in one place.
 *
 * Nobody drives to a mold inspection before the $200 fee is paid. The UI shows
 * Confirm locked; `functions/api/mold/book.ts` REFUSES the confirm outright —
 * the UI lock is a courtesy, this is the rule. Both call this so the reason
 * string a caller sees is the same string the server enforced.
 */
export function canConfirmMoldAppointment(
  fee: MoldFeeState | null | undefined,
): { allowed: boolean; reason: string } {
  return isMoldFeePaid(fee)
    ? { allowed: true, reason: '' }
    : { allowed: false, reason: MOLD_FEE_LOCK_REASON };
}

/** The single sentence shown on the locked Confirm button and returned by the API. */
export const MOLD_FEE_LOCK_REASON =
  `Locked until the $${MOLD_FEE_AMOUNT} inspection fee is paid`;
